/**
 * Full-screen playthrough of MB result: Voronoi map + field charts + year scrubber.
 */
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { MbWellRow } from '@/components/Scenarios/MaterialBalanceWellsSection';
import {
  VoronoiMbMap,
  type VoronoiMapWell,
} from '@/components/Scenarios/VoronoiMbMap';
import type { MaterialBalanceResult, MbPoint } from '@/types/materialBalance';
import type { GeoOrigin } from '@/utils/localKmProjection';
import { mbEngineWellId } from '@/utils/materialBalanceWellData';

const PLAY_MS = 550;

function formatYTick(v: number): string {
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 100 || Number.isInteger(v)) return String(Math.round(v));
  if (abs >= 10) return (Math.round(v * 10) / 10).toString();
  return (Math.round(v * 100) / 100).toString();
}

export interface MaterialBalancePlayDialogProps {
  open: boolean;
  onClose: () => void;
  result: MaterialBalanceResult | null;
  wells: MbWellRow[];
  contour: MbPoint[];
  origin?: GeoOrigin | null;
  so0?: number;
  layerName?: string;
}

export const MaterialBalancePlayDialog: React.FC<MaterialBalancePlayDialogProps> = ({
  open,
  onClose,
  result,
  wells,
  contour,
  origin = null,
  so0 = 0.5,
  layerName,
}) => {
  const years = useMemo(
    () => (result?.field || []).map((r) => r.year).sort((a, b) => a - b),
    [result],
  );
  const yearMin = years[0] ?? result?.year0 ?? 0;
  const yearMax = years[years.length - 1] ?? result?.year1 ?? yearMin;

  const [year, setYear] = useState(yearMin);
  const [playing, setPlaying] = useState(false);
  const scrubbingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setPlaying(false);
      return;
    }
    setYear(yearMin);
    setPlaying(false);
  }, [open, yearMin, result]);

  useEffect(() => {
    if (!playing || !years.length) return;
    const id = window.setInterval(() => {
      setYear((prev) => {
        const i = years.indexOf(prev);
        if (i < 0) return years[0];
        return years[(i + 1) % years.length];
      });
    }, PLAY_MS);
    return () => window.clearInterval(id);
  }, [playing, years]);

  const allMapWells: VoronoiMapWell[] = useMemo(
    () =>
      wells
        .filter((w) => w.x != null && w.y != null)
        .map((w) => ({
          id: mbEngineWellId(w.well_name, w.id),
          x: Number(w.x),
          y: Number(w.y),
          kind: w.kind,
        })),
    [wells],
  );

  const grid = useMemo(
    () => result?.grids?.find((g) => g.year === year) ?? null,
    [result, year],
  );

  const mapWells = useMemo(() => {
    if (!grid) return allMapWells;
    const active = new Set([
      ...(grid.prod || []).map((c) => c.id),
      ...(grid.inj || []).map((c) => c.id),
    ]);
    // Prefer wells active this year; fall back to full list if grid empty
    const filtered = allMapWells.filter((w) => active.has(w.id));
    return filtered.length ? filtered : allMapWells;
  }, [allMapWells, grid]);

  const wellYear = useMemo(() => {
    const block = result?.wells?.find((g) => g.year === year);
    return (block?.wells || []).map((w) => ({ id: w.id, so: w.so }));
  }, [result, year]);

  const chartRows = useMemo(() => {
    const rows = (result?.field || []).filter((r) => r.year <= year);
    return rows.map((r) => ({
      year: r.year,
      oil: r.oil,
      liq: r.liq,
      inj: r.inj,
      np: r.np,
      fw: r.fw ?? 0,
      recovery_factor:
        r.recovery_factor != null ? +(100 * r.recovery_factor).toFixed(2) : 0,
    }));
  }, [result, year]);

  const fieldAtYear = result?.field?.find((r) => r.year === year);

  const stopPlay = () => setPlaying(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ py: 1.25, pr: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            {layerName ? `${layerName} · ` : ''}Material balance · {year}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {fieldAtYear && (
          <Typography variant="caption" color="text.secondary" component="div">
            oil {fieldAtYear.oil} · liq {fieldAtYear.liq} · inj {fieldAtYear.inj} · Np{' '}
            {fieldAtYear.np}
            {fieldAtYear.fw != null ? ` · WC ${fieldAtYear.fw}%` : ''}
            {fieldAtYear.n_prod != null
              ? ` · nP ${fieldAtYear.n_prod} · nI ${fieldAtYear.n_inj ?? 0}`
              : ''}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          py: 1.5,
        }}
      >
        {!result?.field?.length ? (
          <Typography color="text.secondary">
            No simulation result yet. Save wells or properties to run material balance.
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
                gap: 1.5,
              }}
            >
              <VoronoiMbMap
                contour={contour}
                wells={mapWells}
                grid={grid}
                wellYear={wellYear}
                so0={so0}
                origin={origin}
                height="100%"
              />

              <Stack spacing={1} sx={{ minHeight: 0, overflow: 'auto' }}>
                <ChartCard title="Oil · liquid · injection">
                  <ResponsiveContainer width="100%" height={180}>
                    <ComposedChart
                      data={chartRows}
                      margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
                      <XAxis dataKey="year" type="number" domain={[yearMin, yearMax]} tick={{ fontSize: 10 }} />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 10 }}
                        tickFormatter={formatYTick}
                        width={40}
                        label={{
                          value: 'kt / 10³ m³',
                          angle: -90,
                          position: 'insideLeft',
                          fontSize: 10,
                        }}
                      />
                      <Tooltip
                        formatter={(v, name) =>
                          typeof v === 'number'
                            ? [v.toFixed(2), String(name)]
                            : [String(v ?? ''), String(name)]
                        }
                      />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar
                        yAxisId="left"
                        dataKey="inj"
                        name="Injection"
                        fill="#2ca02c88"
                        stroke="#2ca02c"
                        barSize={10}
                        isAnimationActive={false}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="liq"
                        name="Liquid"
                        stroke="#4a7fd4"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="oil"
                        name="Oil"
                        stroke="#1a1a1a"
                        strokeWidth={2.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Water cut">
                  <ResponsiveContainer width="100%" height={120}>
                    <LineChart
                      data={chartRows}
                      margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
                      <XAxis dataKey="year" type="number" domain={[yearMin, yearMax]} tick={{ fontSize: 10 }} />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 10 }}
                        tickFormatter={formatYTick}
                        width={36}
                        unit="%"
                      />
                      <Tooltip
                        formatter={(v) =>
                          typeof v === 'number' ? [`${v.toFixed(1)}%`, 'WC'] : [String(v ?? ''), 'WC']
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="fw"
                        name="WC"
                        stroke="#c62828"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Cumulative oil (Np)">
                  <ResponsiveContainer width="100%" height={120}>
                    <LineChart
                      data={chartRows}
                      margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
                      <XAxis dataKey="year" type="number" domain={[yearMin, yearMax]} tick={{ fontSize: 10 }} />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={formatYTick}
                        width={40}
                        label={{
                          value: 'kt',
                          angle: -90,
                          position: 'insideLeft',
                          fontSize: 10,
                        }}
                      />
                      <Tooltip
                        formatter={(v) =>
                          typeof v === 'number' ? [v.toFixed(2), 'Np'] : [String(v ?? ''), 'Np']
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="np"
                        name="Np"
                        stroke="#6a1b9a"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Stack>
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                flexShrink: 0,
                px: 0.5,
                pt: 0.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <IconButton
                color="primary"
                onClick={() => setPlaying((p) => !p)}
                disabled={years.length < 2}
                aria-label={playing ? 'Pause' : 'Play'}
                size="large"
              >
                {playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Slider
                value={year}
                min={yearMin}
                max={yearMax}
                step={1}
                marks={[
                  { value: yearMin, label: String(yearMin) },
                  { value: yearMax, label: String(yearMax) },
                ]}
                valueLabelDisplay="on"
                valueLabelFormat={(v) => String(v)}
                onChange={(_, v) => {
                  if (scrubbingRef.current || playing) stopPlay();
                  setYear(v as number);
                }}
                onChangeCommitted={() => {
                  scrubbingRef.current = false;
                }}
                onMouseDown={() => {
                  scrubbingRef.current = true;
                  stopPlay();
                }}
                onTouchStart={() => {
                  scrubbingRef.current = true;
                  stopPlay();
                }}
                sx={{
                  flex: 1,
                  mx: 1,
                  mt: 2.5,
                  '& .MuiSlider-valueLabel': {
                    fontSize: 12,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    top: -6,
                    backgroundColor: 'primary.main',
                    '&::before': { display: 'none' },
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                  },
                }}
              />
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 0.75,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="caption" fontWeight={600} sx={{ px: 0.5, display: 'block', mb: 0.25 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}
